import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import PrintDocket from "@/components/PrintDocket";
import { X, Minus, Plus, CheckCircle } from "@phosphor-icons/react";

export default function CartSheet({ open, onClose }) {
  const {
    items,
    setQty,
    remove,
    clear,
    setNote,
    total,
    customerName,
    tableNumber,
    kitchenNotes,
    resetDetails,
  } = useCart();
  const [dietary, setDietary] = useState("");
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const printTimer = useRef(null);
  const queryClient = useQueryClient();
  const isBar = items.length > 0 && items.every((i) => i.menu_type === "drinks");

  useEffect(() => () => clearTimeout(printTimer.current), []);

  const placeOrder = async () => {
    if (!tableNumber.trim()) {
      toast.error("Please enter your table number at the top of the menu");
      return;
    }
    if (items.length === 0) return;
    setPlacing(true);
    try {
      const payload = {
        customer_name: customerName.trim() || "Guest",
        table_number: tableNumber.trim(),
        notes: kitchenNotes.trim(),
        dietary: dietary.trim(),
        order_type: isBar ? "bar" : "kitchen",
        items: items.map((i) => ({
          menu_item_id: i.menu_item_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          notes: (i.notes || "").trim(),
        })),
      };
      const { data } = await api.post("/orders", payload);
      setConfirmed(data);
      clear();
      resetDetails();
      setDietary("");
      // Refresh menu so stock counts / sold-out items update immediately.
      queryClient.invalidateQueries({ queryKey: ["menu"] });
      // Auto-print the docket (kitchen or bar) to the printer as soon as the order is placed.
      printTimer.current = setTimeout(() => window.print(), 400);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPlacing(false);
    }
  };

  const close = () => {
    setConfirmed(null);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={close}
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Panel */}
      <aside
        data-testid="cart-sheet"
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-cream shadow-2xl flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 h-20 border-b border-[#EAE5DE]">
          <h2 className="font-heading text-2xl text-[#2D2825]">
            {confirmed ? "Order sent" : "Your order"}
          </h2>
          <button
            data-testid="close-cart-btn"
            onClick={close}
            className="h-10 w-10 rounded-full hover:bg-[#EAE5DE] flex items-center justify-center transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {confirmed && (
          <div data-testid="order-confirmation" className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <CheckCircle size={72} weight="fill" className="text-forest" />
            <p className="font-heading text-3xl mt-6 text-[#2D2825]">Thank you, it's on its way!</p>
            <p className="text-[#5C544D] mt-3">
              Order <span className="font-mono-kds text-wine">#{confirmed.order_number}</span> for
              table <span className="font-mono-kds">{confirmed.table_number}</span> has been sent to
              the {confirmed.order_type === "bar" ? "bar" : "kitchen"}.
            </p>
            <button
              data-testid="new-order-btn"
              onClick={close}
              className="mt-8 bg-forest text-cream px-8 py-3 rounded-full font-medium hover:bg-[#243027] transition-colors"
            >
              Back to menu
            </button>
          </div>
        )}
        {!confirmed && items.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <p className="font-heading text-2xl text-[#2D2825]">Your order is empty</p>
            <p className="text-[#5C544D] mt-2">Add something delicious from the menu.</p>
          </div>
        )}
        {!confirmed && items.length > 0 && (
          <>
            <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4 space-y-4">
              {items.map((i) => (
                <div
                  key={i.menu_item_id}
                  data-testid={`cart-item-${i.menu_item_id}`}
                  className="bg-white rounded-xl border border-[#EAE5DE] p-3"
                >
                  <div className="flex gap-4 items-center">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2D2825] truncate">{i.name}</p>
                      <p className="font-mono-kds text-wine text-sm">${i.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`decrease-${i.menu_item_id}`}
                        onClick={() => setQty(i.menu_item_id, i.quantity - 1)}
                        className="h-8 w-8 rounded-full border border-[#EAE5DE] flex items-center justify-center hover:bg-[#EAE5DE] transition-colors"
                      >
                        <Minus size={14} />
                      </button>
                      <span data-testid={`cart-qty-${i.menu_item_id}`} className="font-mono-kds w-6 text-center">
                        {i.quantity}
                      </span>
                      <button
                        data-testid={`increase-${i.menu_item_id}`}
                        onClick={() => setQty(i.menu_item_id, i.quantity + 1)}
                        className="h-8 w-8 rounded-full border border-[#EAE5DE] flex items-center justify-center hover:bg-[#EAE5DE] transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <input
                    data-testid={`item-note-${i.menu_item_id}`}
                    value={i.notes}
                    onChange={(e) => setNote(i.menu_item_id, e.target.value)}
                    placeholder="Add a note, e.g. sauce on side"
                    className="mt-2 w-full rounded-lg border border-[#EAE5DE] bg-cream px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-wine"
                  />
                </div>
              ))}
            </div>

            <div className="px-6 pb-2">
              <button
                data-testid="add-more-items-btn"
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 border border-forest text-forest rounded-full py-2.5 text-sm font-medium hover:bg-forest hover:text-cream transition-colors duration-200"
              >
                <Plus size={16} weight="bold" /> Add more items
              </button>
            </div>

            <div className="border-t border-[#EAE5DE] px-6 py-5 space-y-3 bg-cream">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#5C544D]">Table</span>
                <span data-testid="cart-table-label" className="font-mono-kds text-[#2D2825]">
                  {tableNumber.trim() ? `#${tableNumber.trim()}` : "— add at top of menu"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="dietary-input"
                  value={dietary}
                  onChange={(e) => setDietary(e.target.value)}
                  placeholder="Dietary requirements / allergies (optional)"
                  className="col-span-2 rounded-lg border border-wine/40 bg-wine/5 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-wine placeholder:text-wine/60"
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-[#5C544D]">Total</span>
                <span data-testid="cart-total" className="font-mono-kds text-2xl text-[#2D2825]">
                  ${total.toFixed(2)}
                </span>
              </div>
              <button
                data-testid="place-order-btn"
                disabled={placing}
                onClick={placeOrder}
                className="w-full bg-wine text-cream py-4 rounded-full font-medium hover:bg-[#5c252c] transition-colors disabled:opacity-60"
              >
                {placing
                  ? `Sending to ${isBar ? "bar" : "kitchen"}…`
                  : `Place ${isBar ? "bar" : "dine-in"} order`}
              </button>
            </div>
          </>
        )}
      </aside>

      {/* Off-screen docket — auto-prints to the docket printer when an order is placed */}
      <PrintDocket order={confirmed} />
    </>
  );
}
